import { CspError } from '@csp/csp-common-model';
import { noopAsync } from '@csp/csp-common-util';
import { noop } from 'lodash/fp';
import { CaptureMessage } from '../model/CaptureMessage';
import { ExceptionTracker } from '../model/ExceptionTracker';
import { FlushFn } from '../model/FlushFn';
import { TelemetryConfigs } from '../model/TelemetryConfigs';
import { TelemetryServicePlugin } from '../model/TelemetryServicePlugin';
import { TelemetryException } from '../model/TelemetryException';
import { TelemetryConfigsService } from './TelemetryConfigService';

const noopTrackException = (_exception: TelemetryException): string | undefined => undefined;

/** Exported just for testing purposes. */
export const emptyExceptionTracker: ExceptionTracker & CaptureMessage = {
  trackException: noopTrackException,
  captureMessage: noop,
};

let telemetryServicePlugin: TelemetryServicePlugin | undefined = undefined;

const emptyTelemetryService: TelemetryServicePlugin = {
  type: 'Empty',
  init: noop,
  configureScope: noop,
  flush: noop as unknown as FlushFn,
  reset: noopAsync,
  trackException: noopTrackException,
  captureMessage: noop,
};

export const setTelemetryService = <T extends TelemetryServicePlugin>(
  telemetryServicePlugin: T,
  telemetryConfigs: TelemetryConfigs,
): void => {
  TelemetryConfigsService.set(telemetryConfigs);

  if (telemetryConfigs.sentry && telemetryConfigs.sentry.enabled) {
    telemetryServicePlugin.init(telemetryConfigs.sentry);
    initTelemetryService(telemetryServicePlugin);
  }
};

export const getTelemetryService = (): TelemetryServicePlugin => {
  if (TelemetryConfigsService.get().sentry?.enabled) {
    if (!telemetryServicePlugin) {
      throw CspError.badState('Expected initTelemetryService to have been called before getTelemetryService.');
    }
    return telemetryServicePlugin;
  }

  return emptyTelemetryService;
};

export const initTelemetryService = <T extends TelemetryServicePlugin>(_telemetryServicePlugin: T): void => {
  telemetryServicePlugin = _telemetryServicePlugin;
};
